import { ReactElement, useContext } from 'react';
import {
  InputContainer,
  Accordion,
  Icon,
  Tooltip,
  InputLabel,
} from '@betrybe/ada-react';
import { color, hasIcon, monthlyIncomeOptions, textColor } from '@/utils';
import { CheckboxInput, SelectInput } from '@/components/molecules';
import {
  checkAccordionChecked,
  checkAccordionErrors,
} from './checkAccordionErrors';
import { RadioInput } from '@/components/organisms';
import pepOptions from './utils';
import { StudentContext } from '@/contexts';

type AdditionalInformationFormTypes = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: any;
  formType: 'candidate' | 'guarantor';
};

function AdditionalInformationForm(
  props: AdditionalInformationFormTypes,
): ReactElement {
  const { formik, formType } = props;
  const { state } = useContext(StudentContext);
  const isCandidate = formType === 'candidate';
  const accordionError: boolean = checkAccordionErrors({ formik, formType });
  const accordionChecked: boolean = checkAccordionChecked({ formik, formType });
  const isMsc = state.paymentMethod.toLowerCase() === 'msc';
  const cohortRegex = /(\d*)$/g;
  const cohortNumber = cohortRegex.exec(state.processNameAbbreviation) ?? [];

  const checkboxText =
    cohortNumber[0] === '33'
      ? 'Declaro que não possuo financiamentos estudantis e/ou empréstimos consignados cuja(s) parcela(s) ultrapassem o percentual de 13% (treze por cento) da minha renda atual ou, na ausência de renda, 13% (treze por cento) de R$3.000.'
      : 'Declaro que não possuo financiamentos estudantis e/ou empréstimos consignados cuja(s) parcela(s) ultrapassem o percentual de 15% (quinze por cento) da minha renda atual ou, na ausência de renda, 15% (quinze por cento) de R$ 2.500';

  const textHeader = (
    <div className="flex flex-row space-x-2 items-center">
      {hasIcon({ accordionChecked, accordionError })}
      <p
        className={`bold text-base ${textColor({
          accordionChecked,
          accordionError,
        })}`}
      >
        Informações adicionais
      </p>
    </div>
  );

  return (
    <Accordion
      header={textHeader}
      markerColor={color({ accordionChecked, accordionError })}
    >
      <div className="flex flex-col m-6 space-y-5">
        {isCandidate && (
          <>
            <InputContainer
              label="Qual a sua renda mensal atual?"
              error={
                formik.touched.monthlyIncome && formik.errors.monthlyIncome
              }
            >
              {(inputProps) => (
                <div>
                  <div>
                    <SelectInput
                      {...inputProps}
                      id="monthlyIncome"
                      onChange={formik.handleChange}
                      placeholder="Não possuo fonte de renda"
                      value={formik.values.monthlyIncome}
                      name="monthlyIncome"
                      options={monthlyIncomeOptions.map(({ value, name }) => ({
                        label: name,
                        value,
                      }))}
                      errorMessage=""
                      data-testid="monthlyIncome"
                    />
                  </div>
                </div>
              )}
            </InputContainer>
            <input type="hidden" value={`${isMsc}`} name="isMsc" />
            {isMsc && (
              <InputContainer
                label=""
                error={
                  formik.touched.studentFinance && formik.errors.studentFinance
                }
              >
                {(inputProps) => (
                  <>
                    <InputLabel>Financiamento estudantil</InputLabel>
                    <CheckboxInput
                      {...inputProps}
                      id="1"
                      label={checkboxText}
                      name="studentFinance"
                      isControlled={false}
                      disabled={false}
                      checked={formik.values.studentFinance}
                      onChange={(event) => {
                        return event.target.checked
                          ? formik.setFieldValue('studentFinance', 'true')
                          : formik.setFieldValue('studentFinance', 'false');
                      }}
                      value={formik.values.studentFinance}
                      data-testid="studentFinance"
                    />
                  </>
                )}
              </InputContainer>
            )}
          </>
        )}
        <div>
          <InputLabel>
            <>
              {`${
                isCandidate ? 'Você' : 'Essa pessoa'
              } é uma Pessoa Politicamente Exposta (PPE)?`}
              <Tooltip
                backgroundColor="black-800"
                title="Pessoa Politicamente Exposta (PPE) é aquela que desempenha ou desempenhou, nos últimos 5 anos, cargos, empregos ou funções públicas relevantes no Brasil, em outros países ou em territórios estrangeiros."
              >
                <Icon
                  name="help"
                  className="register-container__tooltip-icon"
                  width={18}
                />
              </Tooltip>
            </>
          </InputLabel>
          <RadioInput
            options={pepOptions(isCandidate)}
            label=""
            name="pep"
            id="pep"
            disabled={false}
            errorMessage={formik.touched.pep && formik.errors.pep}
            helpText={undefined}
            onChange={formik.handleChange}
            selected={formik.values.pep.toString()}
            data-testid="pep"
          />
        </div>
      </div>
    </Accordion>
  );
}

export default AdditionalInformationForm;
